/** @jsx jsx */
import React, { useRef, useEffect } from 'react';
import { css, jsx } from '@compiled/react';
import { smoothScrollIntoCenterIfNeeded } from '@jira/issue__scroll-into-viewport/src/index.tsx';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import Form, { ErrorMessage, Field, FormFooter, useFormState } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { useIntl } from '@atlassian/jira-intl';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import messages from './messages.tsx';
import { shouldShowInvalidMessage, canInsert } from './util.tsx';

type InsertForm = {
	url: string;
};

const InsertFormActions = ({ onCancel }: { onCancel: () => void }) => {
	const { formatMessage } = useIntl();
	const formState = useFormState<InsertForm>({
		values: true,
	});
	return (
		<FormFooter>
			<ButtonGroup>
				<Button type="submit" appearance="primary" isDisabled={!canInsert(formState?.values.url)}>
					{formatMessage(messages.insertButton)}
				</Button>
				<Button appearance="subtle" onClick={onCancel}>
					{formatMessage(messages.cancelButton)}
				</Button>
			</ButtonGroup>
		</FormFooter>
	);
};

interface Props {
	insertLoomLinkClickedCount: number;
	loomLinkCount: number;
	onInsert: (url: string) => void;
	onCancel: () => void;
}

interface FormProps {
	url: string;
}

/**
 * This is a very dumb implementation of the Loom Video insert component, and only checks that the supplied link matches a certain regex.
 * Post M2.5 or as a fast follow, we can incorporate searching via Cross Product Search (/gateway/api/xpsearch-aggregator/quicksearch/v1)
 */
export const LoomVideoInsert = ({
	insertLoomLinkClickedCount,
	loomLinkCount,
	onInsert,
	onCancel,
}: Props) => {
	const { formatMessage } = useIntl();
	const resetRef = useRef<(initialValues?: FormProps | undefined) => void>();
	const inputRef = useRef<HTMLDivElement>(null);

	const prevInsertLoomLinkClickedCount = usePrevious<number>(insertLoomLinkClickedCount);
	const prevLoomLinkCount = usePrevious<number>(loomLinkCount);

	const focusOnInput = () => {
		/**
		 * without this, the focus will not work as expected
		 * issue persisted when using requestAnimationFrame instead of setTimeout
		 * root cause is explained here https://hello.atlassian.net/wiki/spaces/~71202082ed214fcb8a401788d49831b11097a6/pages/4011824991/ED-24389+-+React18+editor+focus+issues
		 *  - TLDR: When DropDown PopOver unmounts, useFocusManager forcing document focus.
		 */
		setTimeout(() => {
			if (inputRef.current !== null) {
				inputRef.current.focus({ preventScroll: true });
			}
		}, 100); // 100ms is a safe value to ensure the input is focused and user shouldn't notice the delay
	};

	useEffect(() => {
		// This will hapeen when user has successfully just inserted a link, we should re-focus on the input to let user insert another link
		if (prevLoomLinkCount !== undefined && loomLinkCount > prevLoomLinkCount) {
			focusOnInput();
		}
	}, [loomLinkCount, prevLoomLinkCount]);

	useEffect(() => {
		// this will handle the mount case (where prevInsertLoomLinkClickedCount is undefined) as well as the insert clicked again case
		if (insertLoomLinkClickedCount !== prevInsertLoomLinkClickedCount) {
			smoothScrollIntoCenterIfNeeded(inputRef.current);
			focusOnInput();
		}
	}, [insertLoomLinkClickedCount, prevInsertLoomLinkClickedCount]);

	return (
		<Form<FormProps>
			onSubmit={({ url }) => {
				if (canInsert(url)) {
					const trimmedUrl = url.trim(); // a url with leadning/trailing spaces is valid but we want to trim it before inserting, otherwise the endpoint will reject it
					onInsert(trimmedUrl);
					// reset the text field after submitting
					resetRef.current?.();
				}
			}}
		>
			{({ formProps, reset }) => {
				resetRef.current = reset;
				return (
					<form
						css={formStyles}
						{...formProps}
						data-testid="issue-view-base.content.loom-videos.ui.co-use.loom-video-insert.form"
					>
						<Field
							name="url"
							defaultValue=""
							isRequired
							validate={(value) => (shouldShowInvalidMessage(value) ? 'INVALID_URL' : undefined)}
						>
							{({ fieldProps, error }) => (
								<>
									<Textfield
										ref={inputRef}
										{...fieldProps}
										placeholder={formatMessage(messages.placeholderText)}
										aria-label={formatMessage(messages.placeholderText)}
										autoComplete="off"
									/>
									{error === 'INVALID_URL' && (
										<ErrorMessage>{formatMessage(messages.invalidUrlText)}</ErrorMessage>
									)}
								</>
							)}
						</Field>
						<InsertFormActions onCancel={onCancel} />
					</form>
				);
			}}
		</Form>
	);
};

const formStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& [data-ds--text-field--container]': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		padding: '1px',
	},
});
